// ─────────────────────────────────────────────────────────────
//  SchengenData.js  —  All constants for SchengenChecker flow
// ─────────────────────────────────────────────────────────────
import schengenService from '../../../../services/schengenService';

// ── Step labels ──────────────────────────────────────────────
export const STEPS = [
  { id: 1, label: "Search"      },
  { id: 2, label: "Appointment" },
  { id: 3, label: "Travel Info" },
  { id: 4, label: "Travellers"  },
  { id: 5, label: "Sponsor"     },
  { id: 6, label: "Documents"   },
  { id: 7, label: "Agreement"   },
  { id: 8, label: "Payment"     },
];

// ── Cities for filter ─────────────────────────────────────────
export const CITIES = [
  "All Cities",
  "Delhi",
  "Mumbai",
  "Bangalore",
  "Chennai",
  "Hyderabad",
  "Kolkata",
  "Pune",
  "Ahmedabad",
];

// ── Country short-form aliases (for search) ───────────────────
export const COUNTRY_ALIASES = {
  "FR": "France",
  "DE": "Germany",
  "IT": "Italy",
  "ES": "Spain",
  "NL": "Netherlands",
  "CH": "Switzerland",
  "AT": "Austria",
  "BE": "Belgium",
  "PT": "Portugal",
  "GR": "Greece",
  "SE": "Sweden",
  "NO": "Norway",
  "DK": "Denmark",
  "FI": "Finland",
  "PL": "Poland",
  "CZ": "Czech Republic",
  "HU": "Hungary",
  "SK": "Slovakia",
  "SI": "Slovenia",
  "HR": "Croatia",
  "LU": "Luxembourg",
  "MT": "Malta",
  "LI": "Liechtenstein",
  "IS": "Iceland",
};

// ── Slot results table data ───────────────────────────────────
export const SLOT_RESULTS = [
  {
    id: 1,
    destination: "France",
    flag: "🇫🇷",
    city: "Delhi",
    dates: "15 Jul – 30 Jul 2025",
    slots: true,
    center: "VFS Global – Connaught Place",
    availableDates: [3,4,7,8,11,14,15,18,21,22],
  },
  {
    id: 2,
    destination: "Germany",
    flag: "🇩🇪",
    city: "Mumbai",
    dates: "20 Jul – 05 Aug 2025",
    slots: true,
    center: "BLS International – Nariman Point",
    availableDates: [2,5,6,9,12,16,19,20,23,25],
  },
  {
    id: 3,
    destination: "Italy",
    flag: "🇮🇹",
    city: "Delhi",
    dates: "—",
    slots: false,
    center: null,
    availableDates: [],
  },
  {
    id: 4,
    destination: "Spain",
    flag: "🇪🇸",
    city: "Bangalore",
    dates: "10 Aug – 25 Aug 2025",
    slots: true,
    center: "VFS Global – Indiranagar",
    availableDates: [1,3,6,10,13,17,20,24,27,28],
  },
  {
    id: 5,
    destination: "Netherlands",
    flag: "🇳🇱",
    city: "Chennai",
    dates: "—",
    slots: false,
    center: null,
    availableDates: [],
  },
  {
    id: 6,
    destination: "Switzerland",
    flag: "🇨🇭",
    city: "Delhi",
    dates: "01 Aug – 15 Aug 2025",
    slots: true,
    center: "TLScontact – Vasant Vihar",
    availableDates: [2,4,8,9,13,16,17,21,22,29],
  },
  {
    id: 7,
    destination: "Austria",
    flag: "🇦🇹",
    city: "Hyderabad",
    dates: "—",
    slots: false,
    center: null,
    availableDates: [],
  },
  {
    id: 8,
    destination: "Belgium",
    flag: "🇧🇪",
    city: "Kolkata",
    dates: "05 Aug – 20 Aug 2025",
    slots: true,
    center: "VFS Global – Park Street",
    availableDates: [5,6,7,12,13,14,19,20,26,27],
  },
];

// ── Application centers for step 2 ───────────────────────────
export const APP_CENTERS = [
  {
    id: "c1",
    name: "VFS Global – Connaught Place",
    city: "Delhi",
    address: "6th Floor, Worldmark 1, Aerocity, New Delhi",
    slots: 12,
    availableDates: [3,4,7,8,11,14,15,18,21,22],
  },
  {
    id: "c2",
    name: "TLScontact – Vasant Vihar",
    city: "Delhi",
    address: "B-1/12, Vasant Vihar, South Delhi",
    slots: 6,
    availableDates: [2,5,9,12,16,19,23,26],
  },
  {
    id: "c3",
    name: "BLS International – Karol Bagh",
    city: "Delhi",
    address: "15, Ajmal Khan Road, Karol Bagh, New Delhi",
    slots: 4,
    availableDates: [1,6,10,13,17,20,24,28],
  },
];

// ── Gender options ────────────────────────────────────────────
export const GENDER_OPTIONS = ["Male", "Female", "Other"];

// ── Prior Schengen visits options ─────────────────────────────
export const PRIOR_VISIT_OPTIONS = ["0","1","2","3","4","5+"];

// ── Locale & formatting ──────────────────────────────────────
export const LOCALE = "en-IN";

// ── AOS animation config ─────────────────────────────────────
export const AOS_CONFIG = { duration: 650, easing: "ease-out-cubic", once: true, offset: 60 };

// ── Travel purposes ───────────────────────────────────────────
export const TRAVEL_PURPOSES = [
  {
    id: "tourism",
    label: "Tourism",
    icon: "🏖️",
    desc: "Leisure travel, sightseeing, vacation or holiday in the Schengen area.",
  },
  {
    id: "business",
    label: "Business",
    icon: "💼",
    desc: "Meetings, conferences, trade fairs or professional engagements.",
  },
  {
    id: "family",
    label: "Visiting Family",
    icon: "👨‍👩‍👧",
    desc: "Visiting relatives or friends residing in a Schengen member state.",
  },
];

// ── Sponsor options ───────────────────────────────────────────
export const SPONSOR_OPTIONS = [
  {
    id: "self",
    label: "Self-Sponsored",
    icon: "🙋",
    desc: "I am funding this trip entirely from my own financial resources.",
  },
  {
    id: "other",
    label: "Sponsored by Another Person",
    icon: "🤝",
    desc: "A family member, employer, or another individual is funding this trip.",
  },
];

// ── Sponsor ID types ──────────────────────────────────────────
export const SPONSOR_ID_TYPES = [
  { id: "aadhaar",  label: "Aadhaar Card",  sides: false },
  { id: "passport", label: "Passport",      sides: true  },
  { id: "pan",      label: "PAN Card",      sides: false },
];

// ── Payment methods ───────────────────────────────────────────
export const PAYMENT_METHODS = [
  { id: "debit",      label: "Debit Card",  icon: "💳", convenience: 0  },
  { id: "credit",     label: "Credit Card", icon: "�", convenience: 18 },
  { id: "upi",        label: "UPI",         icon: "�", convenience: 0  },
  { id: "netbanking", label: "Net Banking", icon: "🏦", convenience: 9  },
];

// ── Fee structure ─────────────────────────────────────────────
export const FEE_BASE = {
  appointment: 1999,
  service:     2499,
  gstRate:     0.18,
};

// ── FAQ Data ──────────────────────────────────────────────────
export const FAQ_KEYWORDS = [
  "All Topics",
  "Appointment",
  "Documents",
  "Fees",
  "Processing",
  "Rejection",
  "Travel",
];

export const FAQS = [
  {
    id: 1,
    keyword: "Appointment",
    question: "How do I book a Schengen visa appointment from India?",
    answer:
      "You can book through the official VFS Global, TLScontact, or BLS International portals. Select your destination country, choose a convenient application centre and date, fill in your personal details, and pay the appointment fee online. Our platform streamlines this entire process end-to-end.",
  },
  {
    id: 2,
    keyword: "Appointment",
    question: "How far in advance should I book my Schengen visa appointment?",
    answer:
      "Most Schengen consulates recommend booking at least 3–6 weeks before your intended travel date. During peak summer and Christmas seasons, slots fill up faster—sometimes 8–10 weeks ahead. Always book as early as possible once your travel dates are confirmed.",
  },
  {
    id: 3,
    keyword: "Documents",
    question: "What documents are required for a Schengen visa application?",
    answer:
      "Core documents include a valid passport (min. 6 months validity), recent photographs, flight itinerary, hotel bookings, travel insurance (min. €30,000 coverage), bank statements (last 3–6 months), income proof, and ITR copies. Additional documents vary by country and travel purpose.",
  },
  {
    id: 4,
    keyword: "Documents",
    question: "Is travel insurance mandatory for a Schengen visa?",
    answer:
      "Yes, travel insurance covering a minimum of €30,000 is mandatory for all Schengen visa applications. It must cover medical emergencies, hospitalisation, and repatriation across the entire Schengen zone and be valid for the full duration of your stay.",
  },
  {
    id: 5,
    keyword: "Fees",
    question: "What is the Schengen visa fee for Indian applicants?",
    answer:
      "The standard Schengen visa fee is €90 (approximately ₹8,200). Children aged 6–11 pay a reduced €45. Children under 6 are exempt. Service centre fees (VFS/BLS/TLS) are charged additionally and range from ₹1,500–₹2,500 depending on the centre and city.",
  },
  {
    id: 6,
    keyword: "Fees",
    question: "Are Schengen visa fees refundable if my visa is rejected?",
    answer:
      "No, the Schengen visa fee (€90) is non-refundable regardless of the outcome. However, if an appointment is cancelled well before the scheduled date, some service centres may offer a partial refund of their processing fees. Check the specific refund policy of your chosen centre.",
  },
  {
    id: 7,
    keyword: "Processing",
    question: "How long does Schengen visa processing take from India?",
    answer:
      "Standard processing takes 10–15 working days from the date of biometric submission. Some embassies complete it in 5–7 days during off-peak seasons. Express processing (where available) can take 2–3 days at an additional charge. Always apply well in advance to avoid delays.",
  },
  {
    id: 8,
    keyword: "Processing",
    question: "Can I track my Schengen visa application status?",
    answer:
      "Yes. VFS Global and BLS International provide online tracking portals where you can check real-time status using your application reference number. TLScontact applicants can track via email/SMS updates. Processing stages typically include: Application Received  Under Review  Decision Made  Passport Dispatched.",
  },
  {
    id: 9,
    keyword: "Rejection",
    question: "What are common reasons for Schengen visa rejection?",
    answer:
      "Common rejection reasons include insufficient bank balance, incomplete documents, lack of strong ties to India (proof of returning), inadequate travel insurance, vague travel itinerary, prior overstays, and inconsistencies in the application. A detailed and well-prepared application significantly reduces rejection risk.",
  },
  {
    id: 10,
    keyword: "Rejection",
    question: "Can I reapply after a Schengen visa rejection?",
    answer:
      "Yes, you can reapply immediately after a rejection—there is no mandatory waiting period. However, it's advisable to address the specific reason for rejection (mentioned in the refusal letter) before reapplying. Submitting the same application without changes rarely succeeds.",
  },
  {
    id: 11,
    keyword: "Travel",
    question: "Can I visit multiple Schengen countries on one visa?",
    answer:
      "Yes. A Schengen visa allows free movement across all 27 Schengen member states. Your primary destination should be the country that issues the visa (the one where you'll spend the most days or enter first). Once issued, you can travel freely within the zone for the visa's validity period.",
  },
  {
    id: 12,
    keyword: "Travel",
    question: "What is the maximum duration of stay on a Schengen visa?",
    answer:
      "A single-entry Schengen visa typically allows a maximum stay of 90 days within any 180-day period. A multiple-entry visa may be valid for 1–5 years, but the 90/180-day rule still applies. Overstaying can result in fines, deportation, and bans on future Schengen entry.",
  },
  {
    id: 13,
    keyword: "Appointment",
    question: "What happens if I miss my Schengen visa appointment?",
    answer:
      "Missing your appointment without prior cancellation typically results in forfeiting your appointment fee. You'll need to rebook and pay again. Some centres allow rescheduling with at least 48–72 hours' notice. Repeated no-shows may flag your profile for additional scrutiny.",
  },
  {
    id: 14,
    keyword: "Documents",
    question: "Do I need to submit original documents or copies?",
    answer:
      "Generally, both originals and self-attested photocopies are required. Originals are verified at the application centre and returned to you, except for any documents that the embassy explicitly retains. Always carry organized originals + copies to avoid rejection at the counter.",
  },
];

// ── Document checklist (Step 6) ───────────────────────────────
export const DOCUMENT_PREVIEW = [
  { id: "cover",      label: "Cover Letter",            status: "generating", icon: "📄" },
  { id: "form",       label: "Visa Application Form",   status: "generating", icon: "📋" },
  { id: "photo",      label: "Passport-size Photo",     status: "pending",    icon: "🖼️"  },
  { id: "itinerary",  label: "Travel Itinerary",        status: "pending",    icon: "✈️"  },
  { id: "hotel",      label: "Hotel Bookings",          status: "pending",    icon: "🏨" },
  { id: "insurance",  label: "Travel Insurance",        status: "pending",    icon: "🛡️"  },
  { id: "bank",       label: "Bank Statements",         status: "pending",    icon: "🏦" },
  { id: "itr",        label: "Income Tax Returns",      status: "pending",    icon: "📊" },
  { id: "sponsor",    label: "Sponsorship Letter",      status: "pending",    icon: "📝", isSponsor: true },
];

// ── Month names helper ────────────────────────────────────────
export const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export const DOW_SHORT = ["Mo","Tu","We","Th","Fr","Sa","Su"];

// ── Consent terms ─────────────────────────────────────────────
export const CONSENT_TERMS = [
  {
    id: "t1",
    title: "1. Service Description",
    body: "We provide a platform to: (a) Check availability of Schengen visa appointments; (b) Assist in booking visa appointment slots; (c) Facilitate submission of user details and documents.",
  },
  {
    id: "t2",
    title: "2. User Information & Accuracy",
    body: "You agree that all personal details submitted (name, passport, contact details, etc.) are accurate and complete. Any incorrect or misleading information may result in rejection of appointment or loss of booking.",
  },
  {
    id: "t3",
    title: "3. Document Upload Consent",
    body: "By uploading documents, you confirm that all documents are authentic and belong to you, and grant us permission to store and process your documents solely for appointment booking purposes.",
  },
  {
    id: "t4",
    title: "4. Appointment Booking Disclaimer",
    body: "Appointment availability is controlled by external visa centers / embassies. We do not guarantee any specific slot or date.",
  },
  {
    id: "t5",
    title: "5. Payment Terms",
    body: "All payments are for service facilitation only. Payment does not guarantee visa approval or appointment confirmation. Once payment is made, it is considered final and non-reversible.",
  },
  {
    id: "t6",
    title: "6. No Refund Policy",
    body: "All payments are strictly non-refundable. No refunds will be provided in cases including (but not limited to): slot unavailability, delays in appointment, rejection of visa application, incorrect information submitted by the user, or change of travel plans.",
  },
  {
    id: "t7",
    title: "7. User Responsibility",
    body: "You are responsible for monitoring your email/phone for updates, attending appointments on time, and providing additional documents if required.",
  },
  {
    id: "t8",
    title: "8. Limitation of Liability",
    body: "We are not liable for visa rejections, appointment delays or cancellations, or losses arising from third-party services (embassies, visa centers, etc.).",
  },
  {
    id: "t9",
    title: "9. Data Usage",
    body: "Your data may be used for appointment processing, communication regarding your application, and internal record keeping. We do not sell your personal data to third parties.",
  },
  {
    id: "t10",
    title: "10. Agreement Confirmation",
    body: "By clicking \"I Agree\", you confirm that you have read and understood all terms, you agree to the no-refund policy, and you consent to data collection and processing.",
  },
];

// ── Progress stages definition ────────────────────────────────
export const PROGRESS_STAGES = [
  { id: "paid",       label: "Payment Received",       desc: "Your payment has been processed successfully.",              icon: "💳", color: "#059669" },
  { id: "submitted",  label: "Application Submitted",  desc: "Your application details have been submitted for review.",   icon: "📋", color: "#004d7a" },
  { id: "processing", label: "Under Processing",        desc: "Our team is reviewing and processing your application.",     icon: "⚙️", color: "#d97706" },
  { id: "scheduled",  label: "Appointment Scheduled",  desc: "Your visa appointment slot has been confirmed.",             icon: "📅", color: "#059669" },
  { id: "documents",  label: "Documents Ready",         desc: "Your visa application documents are prepared and ready.",    icon: "📄", color: "#004d7a" },
  { id: "completed",  label: "Completed",               desc: "Your appointment is confirmed. Best of luck for your visa!", icon: "✅", color: "#059669" },
];

// ── Tracking storage helpers ──────────────────────────────────

export async function saveTrackRecord(record) {
  schengenService.save(record).catch(() => {});
}

export async function loadTrackRecord(trackId) {
  try {
    const result = await schengenService.getByTrackId(trackId.trim().toUpperCase());
    if (result.data) return result.data;
  } catch {}
  return null;
}

// ── ID generators ─────────────────────────────────────────────
export function generateTrackId() {
  const prefix = "TVC";
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${ts}-${rand}`;
}

export function generateAppId(idx) {
  return `APP-${String(idx + 1).padStart(3, "0")}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
}

// ── INR formatter ─────────────────────────────────────────────
export function formatINR(n) {
  return "₹" + n.toLocaleString("en-IN");
}

// ── Calendar builder ──────────────────────────────────────────
export function buildCalendarCells(year, month) {
  const first = new Date(year, month, 1).getDay();
  const offset = first === 0 ? 6 : first - 1;
  const days   = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const total  = Math.ceil((offset + days) / 7) * 7;
  return Array.from({ length: total }, (_, i) => {
    const d = i - offset + 1;
    if (d < 1)    return { num: prevDays + d, out: true };
    if (d > days) return { num: d - days,     out: true };
    return { num: d, out: false };
  });
}

// ── Validation helpers ────────────────────────────────────────
export const ALPHA_RE       = /^[A-Za-z\s\-'.]+$/;
export const EMAIL_RE       = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_RE       = /^[\+]?[0-9\s\-()]{8,15}$/;
export const PASSPORT_RE    = /^[A-Z][0-9]{7}$/;
export const PAN_RE         = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

export function validateField(key, value) {
  if (!value || value.toString().trim() === "") return "This field is required";
  const v = value.toString().trim();
  switch(key) {
    case "firstName":
    case "lastName":
    case "placeOfIssue":
    case "emergName":
    case "emergRel":
    case "sponsorName":
    case "sponsorRel":
      if (!ALPHA_RE.test(v)) return "Only letters, spaces, hyphens and apostrophes allowed";
      if (v.length < 2)      return "Minimum 2 characters";
      if (v.length > 60)     return "Maximum 60 characters";
      return null;
    case "email":
      if (!EMAIL_RE.test(v)) return "Enter a valid email address";
      return null;
    case "phone":
    case "emergPhone":
    case "sponsorPhone":
      if (!PHONE_RE.test(v)) return "Enter a valid phone number (8–15 digits)";
      return null;
    case "passportNo":
      if (!PASSPORT_RE.test(v.toUpperCase())) return "Format: 1 letter + 7 digits (e.g. A1234567)";
      return null;
    case "dob":
    case "passportValidity":
      if (!v) return "Select a date";
      return null;
    default:
      return null;
  }
}

export function validateIdNumber(idType, value) {
  if (!value || value.trim() === "") return "ID number is required";
  const v = value.trim().toUpperCase();
  if (idType === "aadhaar") {
    if (!/^[0-9]{12}$/.test(v)) return "Aadhaar must be exactly 12 digits";
  } else if (idType === "pan") {
    if (!PAN_RE.test(v)) return "PAN format: AAAAA9999A (5 letters, 4 digits, 1 letter)";
  } else if (idType === "passport") {
    if (!PASSPORT_RE.test(v)) return "Passport format: 1 letter + 7 digits (e.g. A1234567)";
  }
  return null;
}
